import { useCallback, useContext } from "react";

import { useAsyncCall } from "hooks";

import {
  useEditorData,
  useEditorState,
} from "scenes/Hubly/components/Workspace/components/hooks";
import {
  HubContext,
  ToasterContext,
} from "scenes/Hubly/components/Workspace/Provider";

import { useConditionalItemOrderManager } from "../../../hooks";
import { formatConditionsGroup } from "../../TasksEditor/components/Task/helper";
import { useSubscribeToNextWorkflow } from "./useSubscribeToNextWorkflow";

function useNextWorkflow(workflow) {
  const { addNextWorkflow, deleteNextWorkflow, fetchNextWorkflows } =
    useContext(HubContext);
  const { fireError } = useContext(ToasterContext);
  const [nextWorkflows, dispatch] = useEditorState();
  const { loading, reloadData } = useEditorData(fetchNextWorkflows, {
    id: workflow.id,
    dispatch: dispatch,
  });
  const { running, reorder } = useConditionalItemOrderManager(
    nextWorkflows,
    dispatch,
    reloadData,
    loading,
    "nextWorkflow"
  );
  const [call, isProcessing] = useAsyncCall();

  useSubscribeToNextWorkflow(
    useCallback(() => {
      reloadData();
    }, [reloadData])
  );

  return {
    loading: loading,
    dndInProgress: running,
    reorder: reorder,
    updateLoader: isProcessing,
    nextWorkflows: nextWorkflows,
    reloadNextWorkflow: reloadData,
    createNextWorkflow: useCallback(
      ({ conditionsGroup, ...args }) => {
        return call(async () => {
          try {
            const { id, nextWorkflowName } = await addNextWorkflow({
              workflowId: workflow.id,
              conditionsGroup: formatConditionsGroup(conditionsGroup),
              ...args,
            });
            dispatch({
              type: "add",
              value: {
                id: id,
                nextWorkflow: {
                  id: args.nextWorkflowId,
                  name: nextWorkflowName,
                },
              },
            });

            reloadData();

            return id;
          } catch (error) {
            fireError("Failed to add next workflow", error);
          }

          return null;
        });
      },
      [workflow, call, addNextWorkflow, dispatch, fireError, reloadData]
    ),
    removeNextWorkflow: useCallback(
      (nextWf) => {
        return call(async () => {
          try {
            dispatch({
              type: "edit",
              value: { ...nextWf, beingDeleted: true },
            });

            await deleteNextWorkflow(nextWf.id);

            dispatch({ type: "remove", value: nextWf.id });

            reloadData();
          } catch (error) {
            fireError("Failed to delete next workflow", error);
          }
        });
      },
      [call, deleteNextWorkflow, dispatch, fireError, reloadData]
    ),
    editNextWorkflow: useCallback(
      (value) => {
        dispatch({ type: "edit", value: value });

        reloadData();
      },
      [dispatch, reloadData]
    ),
  };
}

export default useNextWorkflow;
